import { createFileRoute } from '@tanstack/react-router'
import { Navbar, Footer } from '@/components/garmisch'

export const Route = createFileRoute('/_public/terms')({
  component: TermsPage,
})

function TermsPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0B]">
      <Navbar />
      <main className="py-24">
        <div className="mx-auto max-w-[800px] px-6">
          <h1 className="mb-8 font-sans text-4xl font-bold tracking-tight text-gray-900 dark:text-white">
            Terms & Conditions
          </h1>
          <p className="mb-12 text-gray-500 dark:text-white/60">
            Last updated: January 2025
          </p>

          <div className="space-y-12">
            {/* Introduction */}
            <section>
              <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                Introduction
              </h2>
              <p className="leading-relaxed text-gray-600 dark:text-white/70">
                These Terms and Conditions ("Terms") govern your use of
                Garmisch, an open-source Flutter design system, including all
                related documentation, preview applications, and services. By
                using Garmisch, you agree to these Terms.
              </p>
            </section>

            {/* Open Source */}
            <section>
              <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                Open Source License
              </h2>
              <p className="leading-relaxed text-gray-600 dark:text-white/70">
                Garmisch is currently released as open-source software. The
                specific license terms are included in the repository. You are
                free to use, modify, and distribute the software in accordance
                with the applicable open-source license.
              </p>
            </section>

            {/* As-Is Disclaimer */}
            <section>
              <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                "As-Is" Disclaimer
              </h2>
              <p className="leading-relaxed text-gray-600 dark:text-white/70">
                GARMISCH IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND,
                EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES
                OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND
                NONINFRINGEMENT. THE AUTHORS AND COPYRIGHT HOLDERS SHALL NOT BE
                LIABLE FOR ANY CLAIM, DAMAGES, OR OTHER LIABILITY ARISING FROM
                THE USE OF THE SOFTWARE.
              </p>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                Limitation of Liability
              </h2>
              <p className="leading-relaxed text-gray-600 dark:text-white/70">
                In no event shall Garmisch, its contributors, or maintainers be
                liable for any direct, indirect, incidental, special, exemplary,
                or consequential damages (including, but not limited to,
                procurement of substitute goods or services, loss of use, data,
                or profits, or business interruption) however caused and on any
                theory of liability, whether in contract, strict liability, or
                tort (including negligence or otherwise) arising in any way out
                of the use of this software.
              </p>
            </section>

            {/* Acceptable Use */}
            <section>
              <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                Acceptable Use
              </h2>
              <p className="mb-4 leading-relaxed text-gray-600 dark:text-white/70">
                When using Garmisch, you agree to:
              </p>
              <ul className="space-y-2 text-gray-600 dark:text-white/70">
                <li className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#448AFF]" />
                  <span>Comply with all applicable laws and regulations</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#448AFF]" />
                  <span>
                    Respect the intellectual property rights of others
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#448AFF]" />
                  <span>
                    Not use the software for any unlawful or harmful purpose
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#448AFF]" />
                  <span>
                    Not misrepresent your affiliation with the Garmisch project
                  </span>
                </li>
              </ul>
            </section>

            {/* Future Paid Features */}
            <section>
              <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                Future Paid Features
              </h2>
              <p className="leading-relaxed text-gray-600 dark:text-white/70">
                While Garmisch is currently open-source, we may introduce paid
                plugins, premium components, or additional services in the
                future. These paid offerings will be subject to separate terms
                and conditions. The core open-source functionality will remain
                available under the existing license.
              </p>
            </section>

            {/* Modifications */}
            <section>
              <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                Modifications to Terms
              </h2>
              <p className="leading-relaxed text-gray-600 dark:text-white/70">
                We reserve the right to modify these Terms at any time. Changes
                will be effective immediately upon posting to this page. Your
                continued use of Garmisch after any changes constitutes
                acceptance of the new Terms.
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                Governing Law
              </h2>
              <p className="leading-relaxed text-gray-600 dark:text-white/70">
                These Terms shall be governed by and construed in accordance
                with applicable laws, without regard to conflict of law
                principles.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                Contact
              </h2>
              <p className="leading-relaxed text-gray-600 dark:text-white/70">
                For questions about these Terms, please contact us at:
              </p>
              <p className="mt-4 font-mono text-[#448AFF]">
                legal@garmisch.dev
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}
